// src/components/notifications/NotificationProvider.jsx - Notification Stack Container
import React from 'react';
import useNotificationStore from '../../stores/NotificationStore';
import NotificationToast from './NotificationToast';
import { cn } from '../../utils/cn';

/**
 * NotificationProvider - Renders the notification stack
 *
 * Features:
 * - Fixed position in corner (default: top-right)
 * - Stacked layout with spacing
 * - Pause auto-dismiss on hover
 * - Handles multiple notifications
 * - Z-index above everything else
 *
 * Usage:
 *   Place once in App.jsx root (after other providers)
 *   <NotificationProvider position="top-right" />
 */
const NotificationProvider = ({ position = 'top-right' }) => {
  const { getSorted, remove, pause, resume } = useNotificationStore();
  const notifications = getSorted();

  // Handle hover (pause auto-dismiss)
  const handleHover = (id, isHovering) => {
    if (isHovering) {
      pause();
    } else {
      resume();
    }
  };

  // Position classes
  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2'
  };

  // Don't render if no notifications
  if (notifications.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed z-[9999] pointer-events-none',
        positionClasses[position]
      )}
    >
      {/* Notification stack */}
      <div className="flex flex-col gap-2 pointer-events-auto">
        {notifications.map((notification) => (
          <NotificationToast
            key={notification.id}
            notification={notification}
            onDismiss={remove}
            onHover={handleHover}
          />
        ))}
      </div>
    </div>
  );
};

export default NotificationProvider;
